import { useCallback, useEffect, useState } from 'react';
import { hrApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import { RecordDataTable } from '@/components/ui/RecordDataTable';
import { notify } from '@/core/toast/notify';

export function HrLeavePoliciesPage() {
  const [types, setTypes] = useState<Array<{ id: number; name: string; code: string }>>([]);
  const [policies, setPolicies] = useState<unknown[]>([]);
  const [typeName, setTypeName] = useState('');
  const [typeCode, setTypeCode] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [policyName, setPolicyName] = useState('');
  const [accrualJson, setAccrualJson] = useState('{"type":"MONTHLY","days":1}');

  const load = useCallback(async () => {
    try {
      const t = (await hrApi.listLeaveTypes()) as Array<{ id: number; name: string; code: string }>;
      setTypes(t);
      setPolicies(await hrApi.listLeavePolicies());
    } catch (err) {
      showApiError(err, 'Failed to load leave configuration');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addType = async () => {
    try {
      await hrApi.createLeaveType({ name: typeName, code: typeCode, paid: true, carryForward: false });
      notify.success('Leave type created');
      setTypeName('');
      setTypeCode('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create leave type');
    }
  };

  const savePolicy = async () => {
    if (!selectedTypeId || !policyName) return;
    try {
      await hrApi.saveLeavePolicy({
        leaveTypeId: Number(selectedTypeId),
        policyName,
        accrualRuleJson: accrualJson,
      });
      notify.success('Policy saved');
      load();
    } catch (err) {
      showApiError(err, 'Failed to save policy');
    }
  };

  return (
    <div>
      <PageHeader title="Leave policies" description="Configure leave types and accrual rules" breadcrumbs={[{ label: 'HR', to: '/hr' }, { label: 'Leave policies' }]} />
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="card space-y-3 p-4">
          <h3 className="font-semibold">Leave types</h3>
          <Input label="Name" value={typeName} onChange={(e) => setTypeName(e.target.value)} />
          <Input label="Code" value={typeCode} onChange={(e) => setTypeCode(e.target.value)} />
          <Button onClick={() => void addType()}>Add type</Button>
          <ul className="text-sm">{types.map((t) => <li key={t.id}>{t.name} ({t.code})</li>)}</ul>
        </section>
        <section className="card space-y-3 p-4">
          <h3 className="font-semibold">Accrual policy</h3>
          <Select label="Leave type" value={selectedTypeId} onChange={setSelectedTypeId} options={types.map((t) => ({ value: String(t.id), label: t.name }))} placeholder="Select type" />
          <Input label="Policy name" value={policyName} onChange={(e) => setPolicyName(e.target.value)} />
          <Input label="Accrual rule JSON" value={accrualJson} onChange={(e) => setAccrualJson(e.target.value)} />
          <Button onClick={() => void savePolicy()}>Save policy</Button>
          <RecordDataTable data={policies} embedded emptyTitle="No policies configured" />
        </section>
      </div>
    </div>
  );
}
